'use client';

import { FormEvent, useEffect, useState } from 'react';
import {
  createShippingProfile,
  deleteShippingProfile,
  formatPrice,
  getVendorSettings,
  listShippingProfiles,
  updateShippingProfile,
  updateVendorSettings,
  type ShippingProfileKind,
  type VendorSettings,
  type VendorShippingProfile,
} from './api';

export interface VendorShippingPanelProps {
  vendorUserId: string;
  readOnly?: boolean;
}

const emptyProfile = (): Omit<VendorShippingProfile, 'id' | 'stripeShippingRateId'> => ({
  name: '',
  kind: 'flat',
  rateCents: 0,
  displayName: '',
  active: true,
});

export function VendorShippingPanel({ vendorUserId, readOnly = false }: VendorShippingPanelProps) {
  const [settings, setSettings] = useState<VendorSettings | null>(null);
  const [profiles, setProfiles] = useState<VendorShippingProfile[]>([]);
  const [form, setForm] = useState(emptyProfile());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [countriesInput, setCountriesInput] = useState('US');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    getVendorSettings(vendorUserId)
      .then((s) => {
        setSettings(s);
        setCountriesInput((s.allowedShippingCountries ?? ['US']).join(', '));
      })
      .catch(() => setSettings(null));
    listShippingProfiles(vendorUserId)
      .then((r) => setProfiles(r.profiles))
      .catch(() => setProfiles([]));
  }, [vendorUserId]);

  function resetForm() {
    setEditingId(null);
    setForm(emptyProfile());
    setError('');
  }

  function startEdit(profile: VendorShippingProfile) {
    setEditingId(profile.id);
    setForm({
      name: profile.name,
      kind: profile.kind,
      rateCents: profile.rateCents ?? 0,
      zones: profile.zones,
      weightMinGrams: profile.weightMinGrams,
      weightMaxGrams: profile.weightMaxGrams,
      displayName: profile.displayName ?? profile.name,
      active: profile.active,
    });
  }

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        const updated = await updateShippingProfile(vendorUserId, editingId, form);
        setProfiles((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      } else {
        const created = await createShippingProfile(vendorUserId, form);
        setProfiles((prev) => [created, ...prev]);
      }
      resetForm();
      setMessage('Shipping profile saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save shipping profile');
    }
  }

  async function saveDefaults(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setError('');
    const allowedShippingCountries = countriesInput
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    try {
      const saved = await updateVendorSettings(vendorUserId, {
        ...settings,
        allowedShippingCountries,
      });
      setSettings(saved);
      setMessage('Shipping defaults saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save defaults');
    }
  }

  async function setDefaultProfile(profileId: string) {
    if (!settings) return;
    try {
      const saved = await updateVendorSettings(vendorUserId, {
        ...settings,
        defaultShippingProfileId: profileId,
      });
      setSettings(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not set default profile');
    }
  }

  async function removeProfile(profileId: string) {
    await deleteShippingProfile(vendorUserId, profileId);
    setProfiles((prev) => prev.filter((p) => p.id !== profileId));
    if (editingId === profileId) resetForm();
  }

  return (
    <section>
      <h2 className="vendor-console__section-title">Shipping profiles</h2>
      <p className="vendor-console__hint">
        Define shipping tiers, zones, and weight bands. Fixed rates sync to Stripe; dynamic weight
        bands are calculated at checkout.
      </p>
      {message && <p className="text-muted">{message}</p>}
      {error && <p className="form-error">{error}</p>}

      <fieldset disabled={readOnly}>
        <form onSubmit={(e) => void saveDefaults(e)} className="vendor-console__form-grid feed-card settings-page">
          <h3>Store shipping defaults</h3>
          <label>
            Allowed countries (comma-separated ISO codes)
            <input value={countriesInput} onChange={(e) => setCountriesInput(e.target.value)} required />
          </label>
          <label>
            Default profile
            <select
              value={settings?.defaultShippingProfileId ?? ''}
              onChange={(e) => void setDefaultProfile(e.target.value)}
            >
              <option value="">Select default…</option>
              {profiles
                .filter((p) => p.active)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </label>
          <button type="submit" className="btn btn-secondary">
            Save defaults
          </button>
        </form>

        <form onSubmit={(e) => void saveProfile(e)} className="vendor-console__form-grid feed-card settings-page">
          <h3>{editingId ? 'Edit profile' : 'Add shipping profile'}</h3>
          <label>
            Name
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            Checkout label
            <input
              value={form.displayName ?? ''}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Standard shipping"
            />
          </label>
          <label>
            Kind
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as ShippingProfileKind })}
            >
              <option value="flat">Flat rate</option>
              <option value="tier">Tier</option>
              <option value="zone">Zone</option>
              <option value="weight_band">Weight band</option>
            </select>
          </label>
          {(form.kind === 'flat' || form.kind === 'tier' || form.kind === 'zone') && (
            <label>
              Rate (USD)
              <input
                type="number"
                min="0"
                step="0.01"
                value={((form.rateCents ?? 0) / 100).toFixed(2)}
                onChange={(e) =>
                  setForm({ ...form, rateCents: Math.round(Number(e.target.value) * 100) || 0 })
                }
              />
            </label>
          )}
          {form.kind === 'weight_band' && (
            <>
              <label>
                Min weight (grams)
                <input
                  type="number"
                  min="0"
                  value={form.weightMinGrams ?? 0}
                  onChange={(e) => setForm({ ...form, weightMinGrams: Number(e.target.value) })}
                />
              </label>
              <label>
                Max weight (grams)
                <input
                  type="number"
                  min="0"
                  value={form.weightMaxGrams ?? 0}
                  onChange={(e) => setForm({ ...form, weightMaxGrams: Number(e.target.value) })}
                />
              </label>
            </>
          )}
          <label>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />{' '}
            Active
          </label>
          <div className="vendor-console__submit settings-page__submit">
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Save profile' : 'Add profile'}
            </button>
          </div>
        </form>
      </fieldset>

      <ul className="vendor-products__list">
        {profiles.map((profile) => (
          <li key={profile.id} className="vendor-product-card">
            <strong>{profile.name}</strong>{' '}
            <span className="vendor-console__badge">{profile.kind}</span>
            {settings?.defaultShippingProfileId === profile.id && (
              <span className="vendor-console__badge vendor-console__badge--active">Default</span>
            )}
            <p>
              {profile.rateCents != null ? formatPrice(profile.rateCents) : 'Computed at checkout'}
              {profile.stripeShippingRateId ? ` · Stripe ${profile.stripeShippingRateId}` : ''}
            </p>
            {!readOnly && (
              <div className="vendor-console__actions">
                <button type="button" className="btn btn-secondary" onClick={() => startEdit(profile)}>
                  Edit
                </button>
                <button type="button" className="btn btn-link" onClick={() => void removeProfile(profile.id)}>
                  Delete
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
